import {
  OutlineButton,
  SolidButton,
} from "@/components/StoreComponents/StoreButton";
import { useAppSelector } from "@/redux/hooks";
import { Retailer } from "@/redux/types";
import { StoreBrandingType } from "@/types/misc";
import useStoreType from "@/utils/hooks/useStoreType";
import { canResetPassword, openResetPassword } from "@/utils/retailerUtils";
import {
  CheckMarkIcon,
  formatMoney,
  getDefaultBrandclubImage,
  getEntityImage,
  getRetailerFromId,
  TABLET_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { alpha, Box, styled } from "@mui/material";
import { motion } from "motion/react";

const Container = styled(motion.div)(({ theme }) => ({
  position: "relative",

  flexDirection: "column",

  width: "100%",
  overflow: "hidden",
  alignItems: "center",
  display: "flex",
  color: theme.vars.palette.text.primary,
  ".retailer-connect-login-motion-container": {
    padding: "34px 24px 55px 24px",
    [theme.breakpoints.down("md")]: {
      padding: "34px 20px 22px 20px",
    },
  },
  ".reset-password-button": {
    fontSize: 16,
    color: theme.vars.palette.text.primary,
    textAlign: "center",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    textDecoration: "underline",
    marginTop: "12px",
    fontWeight: 600,
    maxWidth: 412,
  },
}));

const HeaderContainer = styled("div")(({ theme }) => ({
  marginTop: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexDirection: "column",
  gap: 12,
  [theme.breakpoints.down("md")]: {
    marginTop: 12,
  },

  ".icons": {
    display: "flex",
    alignItems: "center",
    marginBottom: 12,
    gap: 32,
    [theme.breakpoints.down("md")]: {
      gap: 6,
    },

    ".icons-groups": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      minHeight: 64,
      [theme.breakpoints.down("md")]: {
        minHeight: 52,
      },

      ".icon": {
        position: "relative",
        zIndex: 2,

        "&:not(:first-of-type)": {
          marginLeft: -16,
          [theme.breakpoints.down("md")]: {
            marginLeft: -12,
          },
        },

        "&:nth-of-type(1)": {
          zIndex: 3,
        },
        "&:nth-of-type(2)": {
          zIndex: 2,
        },
        "&:nth-of-type(3)": {
          zIndex: 1,
        },
      },
    },
    ".icon": {
      width: 64,
      backgroundColor: "#ffffff",
      height: 64,
      border: "2px solid #ffffff",
      borderRadius: "999px",
      [theme.breakpoints.down("md")]: {
        width: 52,
        height: 52,
      },
      img: {
        borderRadius: 999,
        width: "100%",
        height: "100%",
        objectFit: "contain",
      },
    },
  },
  ".header-title": {
    fontSize: 20,
    fontWeight: 700,
    color: theme.vars.palette.text.primary,
    textAlign: "center",
    maxWidth: 412,
  },
  ".header-description": {
    fontSize: 16,
    color: theme.vars.palette.text.primary,
    textAlign: "center",
    maxWidth: 412,
  },
}));

const Header = ({
  retailerId,
  costDifference,
}: {
  retailerId?: number;
  costDifference?: number;
}) => {
  const storeType = useStoreType();

  const checkoutLogo = useAppSelector(
    (state) => state.appConfig?.checkoutPageConfig?.logoPicture
  );
  const domainConfig = useAppSelector(
    (state) => state?.appConfig?.domainConfig
  );
  const isDtc = storeType === StoreBrandingType.CustomDTCStore;

  const retailerName = retailerId ? getRetailerFromId(retailerId) : "retailer";

  const dtcBrandLogo = domainConfig?.brandId
    ? checkoutLogo || getDefaultBrandclubImage(domainConfig?.brandId)
    : null;

  return (
    <HeaderContainer>
      <div className="icons">
        {isDtc && dtcBrandLogo ? (
          <div className="icons-groups">
            <div className="icon">
              <img src={dtcBrandLogo} alt="retailer connect" />
            </div>
            <div className="icon">
              <img
                src="https://media.brandclub.com/brandclub/image_asset/brandclub-b-logo.webp"
                alt="retailer connect"
              />
            </div>
            {retailerId ? (
              <div className="icon">
                <img
                  src={getEntityImage(retailerId, "retailer")}
                  alt="retailer connect"
                />
              </div>
            ) : null}
          </div>
        ) : (
          <div className="icons-groups">
            <div className="icon">
              <img
                src="https://media.brandclub.com/brandclub/image_asset/brandclub-b-logo.webp"
                alt="bb logo"
              />
            </div>
            {retailerId ? (
              <div className="icon">
                <img
                  src={getEntityImage(retailerId, "retailer")}
                  alt="retailer connect"
                />
              </div>
            ) : null}
          </div>
        )}
      </div>
      <div className="header-title">
        Wait — don’t miss out! You can still save{" "}
        {costDifference ? formatMoney(costDifference) : "money"} by connecting{" "}
        {retailerName}
      </div>

      <div className="header-description">
        Get the most out of your order by connecting your {retailerName}{" "}
        account.
      </div>
    </HeaderContainer>
  );
};

const BenefitsContent = styled("div")(({ theme }) => ({
  overflowY: "auto",
  flex: 1,
  paddingBottom: 12,
  paddingTop: 12,
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  minWidth: 280,
  // maxWidth: 20,
  // width: "100%",

  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    gap: "5px",
  },
  "&::-webkit-scrollbar": {
    width: 4,
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: alpha(theme.palette.primary.main, 0.2),
    borderRadius: 2,
  },
}));

const BenefitCardRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "2px 2px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    gap: 8,
  },
}));

const BenefitCardTitle = styled("div")(({ theme }) => ({
  fontWeight: 600,
  fontSize: 16,
  color: theme.vars.palette.text.primary,
  marginBottom: 2,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    fontSize: 14,
  },
}));

export const ConnectBenefits = ({
  rewardAmount,
  retailerId,
}: {
  rewardAmount?: number;
  retailerId?: number;
}) => {
  const retailerName = retailerId ? getRetailerFromId(retailerId) : "retailer";

  const benefitList = [
    rewardAmount && rewardAmount > 0
      ? `${formatMoney(rewardAmount)} in cash rewards`
      : "Earn cashback rewards",
    `Fast, free shipping from ${retailerName}`,
    "Hassle-free returns",
    "Securely connect in seconds",
  ];

  return (
    <BenefitsContent>
      {benefitList.map((benefit) => (
        <BenefitCardRow key={benefit}>
          <CheckMarkIcon sx={{ width: 18, height: 18 }} />
          <BenefitCardTitle>{benefit}</BenefitCardTitle>
        </BenefitCardRow>
      ))}
    </BenefitsContent>
  );
};

const SwitchToGuestCheckout = ({
  rewardAmount,
  retailer,
  onRetryLogin,
  onPlaceGuestOrder,
  checkoutIntentEstimateCost,
  checkoutIntentGuestEstimateCost,
}: {
  rewardAmount?: number;
  retailer: Retailer;
  onRetryLogin: () => void;
  onPlaceGuestOrder: () => void;
  checkoutIntentEstimateCost?: {
    subtotal: number;
    shippingFees: number;
    giftCard?: number;
    shippingDiscount: number;
    tax: number;
    serviceFee?: number;
    total: number;
  };
  checkoutIntentGuestEstimateCost?: {
    subtotal: number;
    shippingFees: number;
    giftCard?: number;
    shippingDiscount: number;
    tax: number;
    serviceFee?: number;
    total: number;
  };
}) => {
  const costDifference =
    checkoutIntentEstimateCost && checkoutIntentGuestEstimateCost
      ? checkoutIntentEstimateCost.total - checkoutIntentGuestEstimateCost.total
      : undefined;

  return (
    <Container>
      <Header
        retailerId={retailer.retailerId}
        costDifference={costDifference}
      />
      <ConnectBenefits
        rewardAmount={rewardAmount}
        retailerId={retailer.retailerId}
      />

      <SolidButton
        sx={(theme) => ({
          width: "100%",
          maxWidth: 380,
          marginTop: "12px",
          height: 50,
          fontSize: 16,
          [theme.breakpoints.down("md")]: {
            maxWidth: "100%",
          },
        })}
        onClick={onRetryLogin}
      >
        Retry{" "}
        {retailer.retailerId
          ? getRetailerFromId(retailer.retailerId)
          : "retailer"}{" "}
        login
      </SolidButton>
      <OutlineButton
        onClick={onPlaceGuestOrder}
        sx={(theme) => ({
          width: "100%",
          maxWidth: 380,
          marginTop: "12px",
          height: 50,
          fontSize: 16,
        })}
      >
        Place a guest order
        {costDifference ? ` • ${formatMoney(Math.abs(costDifference))}` : ""}
      </OutlineButton>
      {canResetPassword(retailer) && (
        <Box
          component="button"
          onClick={() => openResetPassword(retailer)}
          className="reset-password-button"
        >
          Reset my {retailer.displayName} password
        </Box>
      )}
    </Container>
  );
};

export default SwitchToGuestCheckout;
